/**
 * Centralized locale configuration
 *
 * Single source of truth for supported locales, types, and utility functions.
 * Used by all apps (frontend + backend) to avoid duplication.
 */

// When updating this list, also update supported-locales.mjs (plain JS copy for vite.config.ts)
export const SUPPORTED_LOCALES = ['en', 'zh', 'id', 'es', 'vi', 'th', 'ja'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = 'en';

/**
 * Check if a language string is a supported locale
 */
export function isSupportedLocale(lang: string): lang is SupportedLocale {
	return (SUPPORTED_LOCALES as readonly string[]).includes(lang);
}

/**
 * Normalize a locale string to a supported locale.
 * Handles variations like 'zh-CN', 'zh_CN', 'en-US', 'in' (old Indonesian code), etc.
 */
export function normalizeLocale(locale: string | undefined | null): SupportedLocale {
	if (!locale) return 'en';

	const lower = locale.toLowerCase();

	if (lower.startsWith('zh')) return 'zh';
	if (lower.startsWith('id') || lower.startsWith('in')) return 'id';
	if (lower.startsWith('es')) return 'es';
	if (lower.startsWith('vi')) return 'vi';
	if (lower.startsWith('th')) return 'th';
	if (lower.startsWith('ja')) return 'ja';
	if (lower.startsWith('en')) return 'en';

	return 'en';
}

/** Regex for matching locale prefix in URL paths (e.g., /en/welcome) */
export const LOCALE_REGEX = new RegExp(`^/(${SUPPORTED_LOCALES.join('|')})`);

/**
 * Extract locale from a URL path like '/zh/pricing' → 'zh'
 * Returns null if no locale prefix found.
 */
export function extractLocaleFromPath(path: string): SupportedLocale | null {
	const match = path.match(LOCALE_REGEX);
	return match ? (match[1] as SupportedLocale) : null;
}

/**
 * Strip the locale prefix from a URL path.
 * '/zh/pricing' → '/pricing', '/en' → ''
 */
export function stripLocaleFromPath(path: string): string {
	return path.replace(LOCALE_REGEX, '');
}
